import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../lib/utils';

const ItemGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col', className)} {...props} />
  ),
);
ItemGroup.displayName = 'ItemGroup';

interface ItemProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'outline';
  asChild?: boolean;
}

const Item = React.forwardRef<HTMLDivElement, ItemProps>(
  ({ className, variant = 'default', asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp
        ref={ref as React.Ref<HTMLDivElement>}
        className={cn(
          'flex items-center gap-3 rounded-[8px] px-3 py-2.5 transition-colors no-underline',
          variant === 'outline' && 'border border-[#E5E5E5] bg-white',
          className,
        )}
        {...props}
      />
    );
  },
);
Item.displayName = 'Item';

interface ItemMediaProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'image' | 'icon';
}

const ItemMedia = React.forwardRef<HTMLDivElement, ItemMediaProps>(
  ({ className, variant = 'icon', ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex-shrink-0 flex items-center justify-center',
        variant === 'image' && 'w-11 h-11 rounded-[6px] overflow-hidden bg-[#F5F5F5]',
        variant === 'icon' && 'w-9 h-9 rounded-full bg-[#F5F5F5]',
        className,
      )}
      {...props}
    />
  ),
);
ItemMedia.displayName = 'ItemMedia';

const ItemContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-1 flex-col min-w-0', className)} {...props} />
  ),
);
ItemContent.displayName = 'ItemContent';

const ItemTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-[14px] font-medium text-[#111827] leading-snug', className)}
    {...props}
  />
));
ItemTitle.displayName = 'ItemTitle';

const ItemDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-[12px] text-[#6B7280] leading-snug mt-0.5', className)}
    {...props}
  />
));
ItemDescription.displayName = 'ItemDescription';

export { ItemGroup, Item, ItemMedia, ItemContent, ItemTitle, ItemDescription };
